import { useUserStore } from '@/store/user';
import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Daybook } from '../api/getDaybookList';
import { decrypt } from '@/utils/decrypt';

import Quill from 'quill';
import 'quill/dist/quill.bubble.css';
import clsx from 'clsx';
import i18n from '@/localisation/i18n';
import { setReadId, SubmitSetReadId } from '@/api/setReadId';
import dayjs from 'dayjs';
import { getDaybookVisualConfig } from './DaybookTypeConfig';

import CustomImage from 'quill/formats/image';

class CustomImageBlot extends CustomImage {
  static create(value: string | { url: string }) {
    const node = super.create(typeof value === 'string' ? value : value.url);

    node.setAttribute(
      'style',
      'max-width: 800px; ' +
        'height: auto; ' +
        'border-radius: 20px; ' +
        'display: block; ' +
        'width: 100%; ' +
        'margin: 20px 0;'
    );

    return node;
  }
}

Quill.register('formats/image', CustomImageBlot, true);

export const ItemDaybook: FC<Daybook & { client: string }> = data => {
  const { addDaybookView, clients } = useUserStore();
  const quillRef = useRef<HTMLDivElement>(null);

  const dataKey = `${data.client}:${data.id}`;
  const [decryptedEntry, setDecryptedEntry] = useState<{
    key: string;
    text: string;
  } | null>(null);

  // Пока расшифровка в полёте (или ключ устарел) — показываем доступный
  // plaintext-fallback, а не пустоту
  const displayText =
    decryptedEntry?.key === dataKey ? decryptedEntry.text : data.text ?? null;

  useEffect(() => {
    let ignore = false;

    const handleDecryption = async () => {
      let text = data.text ?? '';

      if (data.textBin) {
        try {
          text = await decrypt(data.textBin, data.client);
        } catch (error) {
          Sentry.captureException(error);
        }
      }

      if (!ignore) setDecryptedEntry({ key: dataKey, text });
    };

    handleDecryption();

    return () => {
      ignore = true;
    };
  }, [data.text, data.textBin, data.client, dataKey]);

  useEffect(() => {
    if (quillRef.current && displayText) {
      quillRef.current.innerHTML = '';
      const quill = new Quill(quillRef.current, {
        readOnly: true,
        theme: 'bubble',
        modules: {
          clipboard: false,
          toolbar: false,
          history: false,
          scroll: false,
          keyboard: true,
          uploader: false,
        },
      });

      // Устанавливаем данные в редактор
      try {
        quill.setContents(JSON.parse(displayText));
      } catch {
        quill.setText(displayText);
      }
    }
  }, [displayText]);

  const { mutate } = useMutation({
    mutationFn: (data: SubmitSetReadId) => setReadId(data),
    onSuccess: () => {
      addDaybookView(data.client!, data.id);
    },
  });

  const daybooksViewed = clients[data.client!]?.daybooksViewed;
  const isRead = useMemo(() => {
    return daybooksViewed?.has(data.id);
  }, [data.id, daybooksViewed]);

  const setRead = () => {
    if (!isRead) {
      mutate({ user: data.client!, id: data.id });
    }
  };

  const config = useMemo(
    () => getDaybookVisualConfig(data.typeDaybook),
    [data.typeDaybook]
  );

  const formattedTime = useMemo(() => {
    if (!data.creationDate) return null;
    const d = dayjs(data.creationDate);
    return d.isValid() ? d.format('HH:mm') : null;
  }, [data.creationDate]);

  return (
    <Link
      className={clsx(
        'group relative flex min-h-[190px] max-h-[250px] flex-col justify-between overflow-hidden rounded-2xl border p-5',
        'transition-all duration-300 ease-out hover:-translate-y-1',
        config.cardBgClass,
        config.cardBorderClass
      )}
      onClick={setRead}
      to={`${data.id}`}
    >
      {/* Декоративное фоновое свечение в углу */}
      <div
        className={clsx(
          'pointer-events-none absolute -right-6 -top-6 h-28 w-28 rounded-full blur-2xl transition-opacity duration-300 group-hover:opacity-100 opacity-60',
          config.glowClass
        )}
      />

      {/* Фоновый силуэт иконки */}
      <div className="pointer-events-none absolute -bottom-3 -right-3 select-none transition-transform duration-500 ease-out group-hover:scale-110 group-hover:opacity-100 opacity-50">
        {config.watermarkIcon}
      </div>

      {/* Верхняя строка: Бейдж типа + Время + Статус (Новая / Просмотрено) */}
      <div className="relative z-10 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div
            className={clsx(
              'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold tracking-wide backdrop-blur-sm',
              config.badgeClass
            )}
          >
            {config.icon}
            <span>{config.title}</span>
          </div>

          {formattedTime && (
            <span
              className={clsx(
                'text-xs font-medium tracking-wider opacity-75',
                config.timeClass
              )}
            >
              {formattedTime}
            </span>
          )}
        </div>

        <div
          className={clsx(
            'badge badge-sm shrink-0 border text-xs transition-colors',
            isRead
              ? 'border-white/10 bg-white/5 text-additional font-normal'
              : 'border-orange bg-orange text-main font-semibold shadow-[0_0_10px_rgba(242,168,115,0.35)]'
          )}
        >
          {i18n.t(isRead ? 'viewed' : 'new')}
        </div>
      </div>

      {/* Контент: превью Quill с плавным угасанием снизу */}
      <div className="relative z-10 my-3 flex-1 overflow-hidden">
        <div
          className="pointer-events-none line-clamp-4 text-sm leading-relaxed text-white/85 [&_.ql-container]:!border-0 [&_.ql-editor]:!p-0 [&_.ql-editor]:!text-sm [&_.ql-editor]:!text-white/85 [&_.ql-editor]:!leading-relaxed"
          ref={quillRef}
        />
        {/* Градиент угасания длинного текста */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-[#14151e]/90 to-transparent transition-opacity group-hover:opacity-40" />
      </div>

      {/* Нижняя строка: Индикатор перехода */}
      <div className="relative z-10 mt-auto flex items-center justify-between pt-1 text-xs">
        <span className="text-white/40 group-hover:text-white/70 transition-colors flex items-center gap-1">
          <span>Подробнее</span>
          <svg
            className="h-3.5 w-3.5 transform transition-transform duration-300 group-hover:translate-x-0.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M5 12h14" />
            <path d="m12 5 7 7-7 7" />
          </svg>
        </span>
      </div>
    </Link>
  );
};
